import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const HowNFCBusinessCardsWork = () => {
  useSEO({
    title: "How NFC Business Cards Work - Complete Guide | AM CARDMATE Blog",
    description: "Discover how NFC business cards revolutionize networking with instant contact sharing, analytics, and professional features.",
    image: "/assets/connection.jpg",
    keywords: "NFC business cards, how they work, contactless sharing, digital networking, smart cards"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "How NFC Business Cards Work in AM CARDMATE",
        "description": "Complete guide on how NFC business cards revolutionize networking",
        "datePublished": "2025-06-08",
        "dateModified": "2025-06-08",
        "author": { "@type": "Organization", "name": "AM CARDMATE Tech Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/connection.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/how-nfc-business-cards-work" }
      },
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Use NFC Business Cards",
        "description": "Step-by-step guide to using NFC business cards",
        "step": [
          { "@type": "HowToStep", "name": "Download AM CARDMATE", "text": "Get the app from App Store or Google Play" },
          { "@type": "HowToStep", "name": "Create Your Profile", "text": "Design your digital business card in minutes" },
          { "@type": "HowToStep", "name": "Activate Your Card", "text": "Link your NFC card to your profile with a tap" }
        ]
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-purple-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">How NFC Business Cards Work</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-purple inline-flex mb-6">
                <FontAwesomeIcon icon="cogs" />
                <span>How-To Guide</span>
              </div>

              <h1 className="text-h1 mb-6">
                How NFC Business Cards Work
                <span className="block text-gradient-blue-pink mt-2">in AM CARDMATE</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-primary-blue" />
                  Tech Team
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-primary-blue" />
                  June 8, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-primary-blue" />
                  4 min read
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=600&fit=crop" alt="AM CARDMATE NFC Card Demo" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-body-lg text-gray-600 mb-8">
              Ever wondered what happens in that magical moment when you tap your AM CARDMATE NFC card? It's like a secret handshake between your card and a smartphone – except it's way cooler and doesn't require memorizing any complicated moves. Let's pull back the curtain and show you exactly how our NFC business cards turn a simple tap into networking gold! ✨
            </p>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="bolt" className="text-yellow-500" />
                The Magic Number
              </h3>
              <p className="text-body text-gray-600">
                Your entire digital profile loads in under <strong>2 seconds</strong> after tapping. That's faster than a handshake, more hygienic too, and definitely more memorable! No awkward fumbling with phones or spelling out email addresses. Just tap, connect, and impress!
              </p>
            </div>

            {/* Journey of a Tap */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="route" />
                </div>
                <h2 className="text-h3 text-dark">The Journey of a Tap</h2>
              </div>

              <p className="text-body text-gray-600 mb-8">
                Let's follow the incredible journey your information takes in those 2 magical seconds:
              </p>

              <div className="relative pl-8 space-y-6 mb-8">
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-400 to-pink-600"></div>

                {[
                  { time: '0.0s', title: 'The Approach', desc: 'You bring your AM CARDMATE card within 4cm of a smartphone. The phone\'s NFC antenna says "Hello there!" 👋', color: 'bg-purple-400' },
                  { time: '0.1s', title: 'Power Up', desc: "The phone's NFC field powers your card's chip. No battery needed – it's like magic, but it's science! ⚡", color: 'bg-purple-500' },
                  { time: '0.2s', title: 'Data Transfer', desc: 'Your unique profile URL is transmitted from the card to the phone at 424 kbit/s. That\'s your entire digital identity in a fraction of a second! 🚀', color: 'bg-pink-500' },
                  { time: '0.5s', title: 'Browser Launch', desc: 'The phone automatically opens its browser (no app needed!) and navigates to your personalized AM CARDMATE profile. 🌐', color: 'bg-pink-600' },
                  { time: '2.0s', title: 'Connection Complete!', desc: 'Your beautiful digital profile appears, complete with all your contact info, social links, and that professional photo where you actually look good! 🎉', color: 'bg-gradient-to-br from-purple-600 to-pink-600' }
                ].map((step, i) => (
                  <div key={i} className="relative">
                    <div className={`absolute -left-8 w-4 h-4 ${step.color} rounded-full`}></div>
                    <div className={`bg-white rounded-2xl p-4 md:p-6 shadow-lg ${i === 4 ? 'bg-gradient-to-r from-purple-50 to-pink-50' : ''}`}>
                      <h4 className="text-body font-bold text-dark mb-2 flex items-center gap-2">
                        <span className="text-purple-600">{step.time}</span> - {step.title}
                      </h4>
                      <p className="text-body-sm text-gray-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Started Steps */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="rocket" />
                <span>Getting Started</span>
              </div>
              <h2 className="text-h2 mb-6">Get Started in <span className="text-gradient-blue-pink">3 Simple Steps</span></h2>
            </div>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-h4 shadow-lg flex-shrink-0">1</div>
                  <h3 className="text-h4 text-dark">Download AM CARDMATE</h3>
                </div>
                <p className="text-body text-gray-600 mb-6 ml-18">
                  Get our award-winning app and join thousands of professionals who've already revolutionized their networking game. It's free, feature-packed, and ridiculously easy to use.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 ml-18">
                  <a href="https://apps.apple.com/pt/app/am-cardmate/id6747951046" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-all">
                    <FontAwesomeIcon icon={['fab', 'apple']} className="text-xl" />
                    <div className="text-left">
                      <div className="text-caption opacity-80">Download on the</div>
                      <div className="text-btn font-bold">App Store</div>
                    </div>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.amcardmate.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-all">
                    <FontAwesomeIcon icon={['fab', 'google-play']} className="text-xl" />
                    <div className="text-left">
                      <div className="text-caption opacity-80">Get it on</div>
                      <div className="text-btn font-bold">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white font-bold text-h4 shadow-lg flex-shrink-0">2</div>
                  <h3 className="text-h4 text-dark">Create Your Digital Profile</h3>
                </div>
                <p className="text-body text-gray-600 mb-6 ml-18">
                  Design a stunning digital business card that represents the best version of professional you. It takes less than 3 minutes!
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 ml-18">
                  {[
                    { icon: 'user', title: 'Contact Info', desc: 'Add your details', gradient: 'from-blue-400 to-purple-600', bg: 'from-blue-50 to-purple-50' },
                    { icon: 'share-alt', title: 'Social Links', desc: 'Connect everything', gradient: 'from-purple-400 to-pink-600', bg: 'from-purple-50 to-pink-50' },
                    { icon: 'palette', title: 'Customize', desc: 'Make it yours', gradient: 'from-pink-400 to-red-600', bg: 'from-pink-50 to-red-50' }
                  ].map((item, i) => (
                    <div key={i} className={`bg-gradient-to-br ${item.bg} rounded-xl p-4 text-center hover:shadow-md transition-all`}>
                      <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white mx-auto mb-3`}>
                        <FontAwesomeIcon icon={item.icon} className="text-xl" />
                      </div>
                      <h4 className="text-body-sm font-semibold mb-1">{item.title}</h4>
                      <p className="text-caption text-gray-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-h4 shadow-lg flex-shrink-0">3</div>
                  <h3 className="text-h4 text-dark">Activate Your NFC Card</h3>
                </div>
                <p className="text-body text-gray-600 mb-6 ml-18">
                  When your sleek NFC card arrives, linking it takes seconds. One tap and you're ready to network like a pro!
                </p>
                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6 ml-18">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'mobile-alt', title: 'Open App', desc: 'Launch AM CARDMATE' },
                      { icon: 'plus-circle', title: 'Add Card', desc: 'Tap "Link New Card"' },
                      { icon: 'wifi', title: 'Hold & Tap', desc: 'Card to phone' },
                      { icon: 'check', title: "You're Live!", desc: 'Start networking', isGreen: true }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${item.isGreen ? 'bg-gradient-to-br from-green-400 to-teal-600 text-white' : 'bg-green-100 text-green-600'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <FontAwesomeIcon icon={item.icon} />
                        </div>
                        <div>
                          <div className="text-body-sm font-semibold">{item.title}</div>
                          <div className="text-caption text-gray-600">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What They See */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                <FontAwesomeIcon icon="eye" />
              </div>
              <h2 className="text-h3 text-dark">What Your New Connection Sees</h2>
            </div>

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="ml-4 text-body-sm">amcardmate.com/uniquelink</span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="text-center mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full mx-auto mb-4"></div>
                  <h3 className="text-h3 text-dark mb-2">Your Name</h3>
                  <p className="text-body text-gray-600">Your Amazing Title @ Cool Company</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  {[
                    { icon: 'phone', label: 'Call' },
                    { icon: 'envelope', label: 'Email' },
                    { icon: ['fab', 'linkedin'], label: 'LinkedIn' },
                    { icon: 'download', label: 'Save Contact', primary: true }
                  ].map((action, i) => (
                    <button key={i} className={`py-3 px-4 rounded-xl text-btn-sm ${action.primary ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white col-span-2 sm:col-span-1' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-all`}>
                      <FontAwesomeIcon icon={action.icon} className="mr-2" />
                      {action.label}
                    </button>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-body text-gray-600 italic">
                    "Your compelling bio that makes people want to connect with you immediately."
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                <h4 className="text-card-title text-dark mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon="mobile-alt" className="text-primary-blue" />
                  No App Required
                </h4>
                <p className="text-card-body">
                  Recipients don't need to download anything. It opens right in their browser, making it friction-free and universally accessible!
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                <h4 className="text-card-title text-dark mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon="bolt" className="text-purple-600" />
                  Instant Actions
                </h4>
                <p className="text-card-body">
                  One-tap calling, emailing, or saving to contacts. We've eliminated every possible barrier to connection!
                </p>
              </div>
            </div>

            {/* Pro Features */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="star" />
                </div>
                <h2 className="text-h3 text-dark">Features That Make You Look Like a Pro</h2>
              </div>

              <div className="space-y-4">
                {[
                  { icon: 'sync', title: 'Real-Time Updates', desc: "Got promoted? New phone number? Update your profile once, and everyone who has your card sees the new info instantly.", color: 'from-orange-400 to-orange-600' },
                  { icon: 'chart-line', title: 'Analytics Dashboard', desc: "See who viewed your profile, when they viewed it, and what they clicked. It's like having X-ray vision for your networking efforts!", color: 'from-purple-400 to-purple-600' },
                  { icon: 'layer-group', title: 'Multiple Profiles', desc: "Business you, casual you, conference you – create different profiles for different contexts and switch between them instantly!", color: 'from-blue-400 to-blue-600' },
                  { icon: 'qrcode', title: 'Backup QR Code', desc: "For those rare moments when someone's NFC is turned off, every card has a QR code backup. We've got you covered!", color: 'from-green-400 to-green-600' }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div>
                        <h4 className="text-card-title text-dark mb-2">{item.title}</h4>
                        <p className="text-card-body">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="question-circle" />
                </div>
                <h2 className="text-h3 text-dark">Quick Answers</h2>
              </div>

              <div className="space-y-4">
                {[
                  { q: 'Does it work with all phones?', a: "Yes! All smartphones made after 2015 have NFC. That's iPhone 6 and up, and pretty much any Android from the last decade. We're talking 99% coverage!" },
                  { q: 'What if their NFC is turned off?', a: 'No worries! Every card has a QR code on the back. Same result, just with a quick camera scan instead of a tap.' },
                  { q: 'Can I change my info after linking the card?', a: "Absolutely! That's the beauty of it. Update your profile anytime through the app, and your card automatically shares the new info." },
                  { q: 'Is it secure?', a: 'Fort Knox secure! Your card only contains a link to your profile, not your actual data. Plus, you control exactly what information is visible.' }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-card-title text-dark mb-2">{item.q}</h4>
                    <p className="text-card-body">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-4">Ready to Experience the Magic?</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of professionals who've already upgraded their networking game. Your next great connection is just a tap away!
            </p>
            <div className="grid grid-cols-3 gap-8 max-w-xl mx-auto mb-8">
              {[
                { value: '0.2s', label: 'Connection Time' },
                { value: '∞', label: 'Profile Updates' },
                { value: '100%', label: 'Awesome Factor' }
              ].map((stat, i) => (
                <div key={i} className="text-center text-white">
                  <div className="text-h3 font-bold mb-1">{stat.value}</div>
                  <div className="text-body-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nfc-cards" className="btn bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="shopping-cart" />
                <span>Get Your NFC Card</span>
              </Link>
              <Link to="/personal-card" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="user" />
                <span>Check All Features</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">TT</div>
                <div>
                  <h3 className="text-card-title text-dark">Tech Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Development Team</p>
                  <p className="text-body-sm text-gray-600">
                    Our brilliant team of engineers and designers who make magic happen behind the scenes. We're the ones who turn "wouldn't it be cool if..." into "holy cow, it actually works!"
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Continue Your Journey</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/what-is-nfc-technology', title: 'What is NFC Technology?', gradient: 'from-blue-400 to-blue-600' },
                  { to: '/blog/setting-up-digital-business-card', title: 'Setting Up Your First Digital Card', gradient: 'from-blue-500 to-purple-600' },
                  { to: '/blog/creative-ways-use-nfc-card', title: '10 Creative Ways to Use NFC', gradient: 'from-orange-400 to-red-600' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowNFCBusinessCardsWork;